import { ArenaGameState } from './ArenaTypes';
import {
  ArenaGameStateV2,
  CoreTableState,
  BidWhistGameExt,
  SpadesGameExt,
  TonkGameExt,
  GameExtension
} from './CoreTableState';

/**
 * Universal table state keys that belong strictly to CoreTableState.
 * Game mechanics (bids, trump, tricks, books, bags, stock, spreads, etc.)
 * are deliberately excluded from this set and extracted into GameExtension.
 */
const CORE_STATE_KEYS = new Set<string>([
  'tableId', 'tableName', 'isPrivate', 'inviteCode', 'phase', 'dealerSeat',
  'setRule', 'gameType', 'cardGameType', 'isQuickMatch', 'botEngine',
  'readyMode', 'seats', 'team1Score', 'team2Score', 'targetScore',
  'playerScores', 'seatScores', 'seatPoints', 'pointsTaken',
  'team1GamesWon', 'team2GamesWon', 'handNumber', 'scoreHistory',
  'seriesMode', 'series', 'hands', 'revealedHands', 'handSnapshots', 'currentTurnSeat',
  'isHandComplete', 'createdBy', 'hostId', 'createdAt', 'updatedAt',
  'turnStartedAt', 'expireAt', 'lastActionMessage', 'botJustification',
  'lastBotReaction', 'presence', 'revision', 'tournamentId',
  'tournamentMatchId', 'broadcastSenderUid', 'gameConfig', 'gameExt'
]);

/**
 * Resolves explicit gameType discriminator from state metadata.
 */
function resolveGameType(state: Partial<ArenaGameState>): 'BID_WHIST' | 'SPADES' | 'HEARTS' | 'TONK' {
  const raw = String(state.cardGameType || state.gameType || '').toUpperCase();
  if (raw.includes('SPADES') || raw === '500_PTS' || raw === '250_PTS' || raw === '300_PTS') return 'SPADES';
  if (raw.includes('HEARTS') || raw === '100_PTS' || raw === '50_PTS') return 'HEARTS';
  if (raw.includes('TONK')) return 'TONK';
  return 'BID_WHIST';
}

/**
 * Adapts incoming game state into a decoupled CoreTableState + gameExt structure,
 * while preserving legacy properties for 100% backwards compatibility during transition.
 * Core state strictly contains universal table properties; game-specific mechanics
 * (bidding, trump, tricks/books, stock, spreads) are housed in the discriminated gameExt.
 */
export function adaptTableState(state: ArenaGameState | null | undefined): (ArenaGameState & { gameExt: GameExtension }) | null {
  if (!state) return null;

  const resolvedGameType = resolveGameType(state);
  const gameExt: Record<string, unknown> = {
    gameType: resolvedGameType
  };

  const rawState = state as unknown as Record<string, unknown>;
  const existingExt = rawState.gameExt || rawState.gameExtension;
  if (existingExt && typeof existingExt === 'object') {
    Object.assign(gameExt, existingExt);
  }

  // Agnostically populate any non-core state properties into gameExt
  for (const [key, value] of Object.entries(rawState)) {
    if (!CORE_STATE_KEYS.has(key) && value !== undefined) {
      gameExt[key] = value;
    }
  }

  // Ensure gameType discriminator is always explicitly assigned
  if (!gameExt.gameType) {
    gameExt.gameType = resolvedGameType;
  }

  // Preserve all properties on root state and attach typed gameExt (ensuring gameExt properties are accessible on root)
  return Object.assign({}, gameExt, state, { gameExt: gameExt as unknown as GameExtension });
}
